import { FC, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "./ShareOfSearchPreview.module.scss";
import {
  Preloader,
  ChartButton,
  PieChartWithInnerLabels,
  LineChart,
  ErrorPlaceholder,
} from "../../../components";
import { onErrorHandler } from "../../helpers";
import { WidgetChart } from "../../types";
import { useWidgetFetching, useWidgetPreview } from "../../hooks";
import { NUMBER_MEASUREMENT_PERCENTAGE } from "../../constants";

// Inner imports
import { WIDGET_ID } from "./constants";

export const ShareOfSearchPreview: FC<WidgetsView.Preview> = ({
  searchId,
  setChartRef,
  onChartClicked,
  dashboardId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { widgetData, isLoading } = useWidgetFetching(searchId, WIDGET_ID);

  const { lineChartData, pieChartData, brands } = useMemo(() => {
    const { lineChart = [], pieChart = {}, brands = [] } = widgetData || {};

    return {
      lineChartData: lineChart,
      pieChartData: pieChart,
      brands,
    };
  }, [widgetData]);

  const charts: WidgetChart[] = useMemo(() => {
    const charts: WidgetChart[] = [];

    if (pieChartData && Object.keys(pieChartData)?.length) {
      const pieChartValue =
        pieChartData["oneYear"]?.values ||
        pieChartData["sixMonths"]?.values ||
        pieChartData["threeMonths"]?.values ||
        pieChartData["twoMonths"]?.values ||
        pieChartData["oneYear"]?.values;

      charts.push({
        type: "pieChart",
        chart: (
          <PieChartWithInnerLabels
            className={styles.chart}
            data={pieChartValue}
            fitTo="height"
          />
        ),
      });
    }

    if (lineChartData?.length) {
      charts.push({
        type: "lineChart",
        chart: (
          <LineChart
            className={styles.chart}
            labels={brands}
            data={lineChartData}
            viewMode="widgetPreview"
            searchId={searchId}
            chartStyles={{
              lineStrokeWidth: 3,
            }}
            chartSettings={{
              hasTrendLine: true,
            }}
            axis={{
              yAxisLegend: t("w_share_of_search_title"),
              yAxisUnit: NUMBER_MEASUREMENT_PERCENTAGE,
              yAxisVerticalPadding: { top: 10, bottom: 0 },
            }}
          />
        ),
      });
    }

    return charts;
  }, [pieChartData, lineChartData, brands, searchId, t]);

  const { widgetViews: views, viewIndex, setViewIndex } = useWidgetPreview({
    charts,
    searchId,
    widgetId: WIDGET_ID,
    dashboardId,
  });

  if (isLoading) return <Preloader type="bar" modifier="inner" />;

  return (
    <div className={styles.widgetPreview}>
      {views.length ? (
        <>
          <div className={styles.buttons}>
            {views.map(({ buttonIcon }, i) =>
              i !== viewIndex ? (
                <ChartButton
                  className={styles.button}
                  icon={buttonIcon}
                  onButtonClick={() => setViewIndex(i)}
                  key={buttonIcon + i}
                  isEventPropagationStopped
                />
              ) : null,
            )}
          </div>
          <div
            ref={(chartRef) => chartRef && setChartRef(chartRef)}
            className={styles.chartWrapper}
            onClick={onChartClicked}
          >
            {views[viewIndex]?.chart}
          </div>
        </>
      ) : (
        <ErrorPlaceholder
          onMountCallBack={() =>
            onErrorHandler(setChartRef, searchId, WIDGET_ID, dispatch)
          }
        />
      )}
    </div>
  );
};
